import React from 'react';

// The records behind one reason on one proposal.
//
// A reason is a sentence — "24 roles on your books here". A sentence a user cannot
// check is a claim, not a rationale, and taking claims on trust is exactly what this
// queue's redesign set out to avoid. This dialog is where the sentence opens into the
// twenty-four rows.
//
// It shows what the API returns and nothing more: `total` is the true count while
// `rows` is capped, so a company with 400 roles is reported honestly as "showing 50
// of 400" rather than quietly truncated to look complete.

// Column headings per reason. The API returns a deliberately generic three-column
// shape (primary, secondary, at) so one dialog serves every reason; only the labels
// differ, and they live here rather than on the wire.
export const EVIDENCE_COLUMNS = {
  open_role_volume: { primary: 'Role', secondary: 'Location', at: 'Started' },
  contact_density: { primary: 'Person', secondary: 'Title', at: '' },
  recorded_contact: { primary: 'Activity', secondary: 'Note', at: 'When' },
};

const DAY = { day: 'numeric', month: 'short', year: 'numeric' };

// `columns` lets a second caller supply its own headings without adding its
// codes to the map above. The Candidate Graph reuses this dialog — the wire
// shape and the honest "showing N of M" are identical, and a second dialog would
// be a second thing to keep consistent — but its reason codes are its own.
export default function EvidenceDialog({ title, code, evidence, loading, error, onClose, columns }) {
  const cols = columns || EVIDENCE_COLUMNS[code] || { primary: 'Detail', secondary: '', at: 'When' };
  const rows = evidence?.rows || [];
  const total = evidence?.total || 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-6"
      role="dialog" aria-modal="true" aria-label={`Detail for ${title}`}>
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl overflow-hidden flex flex-col max-h-[80vh]">
        <header className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {!loading && !error && (
            <p className="text-sm text-gray-500 mt-0.5">
              {rows.length < total
                ? `Showing ${rows.length} of ${total}`
                : `${total} ${total === 1 ? 'record' : 'records'}`}
            </p>
          )}
        </header>

        <div className="flex-1 overflow-y-auto">
          {loading && <p className="px-5 py-8 text-center text-sm text-gray-500">Loading…</p>}

          {error && (
            <p className="m-5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </p>
          )}

          {!loading && !error && rows.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-gray-500">Nothing on record.</p>
          )}

          {!loading && !error && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th scope="col" className="px-5 py-2 font-medium">{cols.primary}</th>
                  {cols.secondary && <th scope="col" className="px-5 py-2 font-medium">{cols.secondary}</th>}
                  {cols.at && <th scope="col" className="px-5 py-2 font-medium">{cols.at}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.primary}-${r.at || ''}-${i}`} className="border-b border-gray-50 last:border-b-0">
                    <td className="px-5 py-2 text-gray-900">{r.primary}</td>
                    {cols.secondary && <td className="px-5 py-2 text-gray-500">{r.secondary || '—'}</td>}
                    {cols.at && (
                      <td className="px-5 py-2 text-gray-500 whitespace-nowrap">
                        {r.at ? new Date(r.at).toLocaleDateString('en-GB', DAY) : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600">
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
